import { Head, Link, usePage } from "@inertiajs/react"
import { Coffee, ArrowUp, ArrowLeft } from "lucide-react"
import { useState, useRef, useEffect } from "react"

import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { signInPath, dashboardPath } from "@/routes"

interface Message {
  role: "user" | "assistant"
  content: string
}

interface ChatShowProps {
  question: string
  answer: string
  questionId?: string
  auth?: {
    user?: {
      id: number
      name: string
      email: string
    }
  }
}

export default function ChatShow({ question, answer }: ChatShowProps) {
  const page = usePage()
  const { auth } = page.props as { auth: ChatShowProps["auth"] }

  const [messages, setMessages] = useState<Message[]>([
    { role: "user", content: question },
    { role: "assistant", content: answer },
  ])
  const [input, setInput] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!input.trim() || isLoading) return

    const userMessage = input.trim()
    setInput("")
    setIsLoading(true)

    // Add user message
    setMessages((prev) => [...prev, { role: "user", content: userMessage }])

    // Simulate AI response (replace with actual API call)
    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "Esta es una respuesta simulada. Aquí integrarías tu API de IA para generar respuestas reales basadas en el contexto de café.",
        },
      ])
      setIsLoading(false)
    }, 1000)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto"
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }, [input])

  return (
    <>
      <Head title={`Chat - ${question}`} />

      <div className="bg-background flex min-h-screen flex-col">
        {/* Header */}
        <header className="bg-background/95 supports-[backdrop-filter]:bg-background/60 sticky top-0 z-10 border-b backdrop-blur">
          <div className="container mx-auto flex h-14 items-center justify-between px-4">
            <div className="flex items-center gap-3">
              <Link href="/chat">
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <Coffee className="text-primary h-5 w-5" />
                <span className="font-serif text-lg font-semibold">
                  Catador Pro
                </span>
              </div>
            </div>
            <div>
              {auth?.user ? (
                <Link href={dashboardPath()}>
                  <Button size="sm" variant="outline">
                    Dashboard
                  </Button>
                </Link>
              ) : (
                <Link href={signInPath()}>
                  <Button size="sm">Iniciar sesión</Button>
                </Link>
              )}
            </div>
          </div>
        </header>

        {/* Messages */}
        <main className="flex-1 overflow-y-auto">
          <div className="container mx-auto max-w-3xl px-4 py-8">
            <div className="space-y-6">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      message.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-foreground"
                    }`}
                  >
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">
                      {message.content}
                    </p>
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-muted max-w-[80%] rounded-2xl px-4 py-3">
                    <div className="flex gap-1">
                      <div className="bg-muted-foreground h-2 w-2 animate-bounce rounded-full [animation-delay:-0.3s]" />
                      <div className="bg-muted-foreground h-2 w-2 animate-bounce rounded-full [animation-delay:-0.15s]" />
                      <div className="bg-muted-foreground h-2 w-2 animate-bounce rounded-full" />
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>
        </main>

        {/* Input */}
        <div className="bg-background/95 supports-[backdrop-filter]:bg-background/60 sticky bottom-0 border-t backdrop-blur">
          <div className="container mx-auto max-w-3xl px-4 py-4">
            <form onSubmit={handleSubmit} className="relative">
              <div className="bg-card flex items-end gap-2 rounded-3xl border p-2 shadow-sm">
                <Textarea
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Escribe tu mensaje..."
                  className="max-h-32 min-h-[40px] resize-none border-0 bg-transparent px-3 py-2 focus-visible:ring-0"
                  rows={1}
                />
                <Button
                  type="submit"
                  size="icon"
                  className="h-10 w-10 shrink-0 rounded-full"
                  disabled={!input.trim() || isLoading}
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
              </div>
            </form>
            <p className="text-muted-foreground mt-2 text-center text-xs">
              Catador Pro puede cometer errores. Verifica información
              importante.
            </p>
          </div>
        </div>
      </div>
    </>
  )
}
